import { useMemo } from "react";
import {
    countText,
    formatNumber,
    formatUAHNumber,
    formatUAHStandard,
    isMotivationGroupA,
    isMotivationGroupP,
    pluralizeUk,
} from "tm-shared/utils";

import { formatValueIndicator, getMotivationImage } from "@/domains/kpi";
import { DashboardCollapseListItem } from "@/features/Dashboard/components/DashboardCollapse";
import { PrimaryText } from "@/shared/ui/Typography";

import type { SelfPlanModalState } from "../../../types";
import { SelfPlanModalType } from "../../../types";
import { KPIMetadata } from "../utils";

const MEASUREMENTS = {
    b: "пл.",
    l: "л.",
    m: "грн.",
    p: "ТТ",
    a: "СКЮ",
} as const;

export const useKPICollapseItems = (
    metadata: KPIMetadata,
): DashboardCollapseListItem<SelfPlanModalState>[] => {
    const {
        group,
        groupType,
        name,
        motivationType,
        finGroupDetails,
        salaryPercent,
        planGrowthCoefficient,
        factCount,
        planCount,
        percent,
        bonus,
        progressive,
        minComplete,
        maxComplete,
        isProgressive,
    } = metadata;

    const finGroupsItem =
        useMemo((): DashboardCollapseListItem<SelfPlanModalState> | null => {
            if (groupType === "common") return null;

            const isSingle = finGroupDetails.length === 1;

            return {
                label: `Груп${isSingle ? "а" : "и"} фін. обліку:`,
                value: isSingle
                    ? finGroupDetails[0]
                    : `${finGroupDetails[0]} + ${finGroupDetails.length - 1}`,
                info: isSingle
                    ? undefined
                    : {
                          type: SelfPlanModalType.finGroups,
                          payload: { title: name, finGroupDetails },
                      },
            };
        }, [groupType, finGroupDetails, name]);

    const scaleItems =
        useMemo((): DashboardCollapseListItem<SelfPlanModalState>[] => {
            const maxCompleteValue =
                maxComplete === null ? "∞" : `${maxComplete} %`;

            if (isProgressive && progressive && progressive.length > 0) {
                const firstStep = progressive[0];
                const lastStep = progressive[progressive.length - 1];

                if (firstStep && lastStep) {
                    return [
                        {
                            label: "Перший крок:",
                            value: `${firstStep.from} % - ${firstStep.coefficient}`,
                        },
                        {
                            label: "Останній крок:",
                            value: `${lastStep.from}% - ${lastStep.coefficient}`,
                        },
                    ];
                }
            }

            return [
                {
                    label: "Зараховується від:",
                    value: `${minComplete ?? 0} %`,
                },
                { label: "Зараховується до:", value: maxCompleteValue },
            ];
        }, [isProgressive, progressive, minComplete, maxComplete]);

    const collapseItems =
        useMemo((): DashboardCollapseListItem<SelfPlanModalState>[] => {
            const baseItems = finGroupsItem ? [finGroupsItem] : [];

            if (motivationType === "s") {
                return [
                    ...baseItems,
                    {
                        label: "Сума продажу:",
                        value: formatUAHStandard(factCount),
                    },
                    { label: "Відсоток бонусу:", value: `${salaryPercent} %` },
                    {
                        label: "Бонус:",
                        value: formatUAHStandard(bonus.current),
                    },
                ];
            }

            const measurement = MEASUREMENTS[motivationType];

            const bonusValue = [bonus.current, bonus.max]
                .filter((value) => value !== null)
                .map((value) => formatUAHNumber(+value))
                .join(" / ");

            const scaleTypeItem: DashboardCollapseListItem<SelfPlanModalState> =
                {
                    label: "Тип розрахунку:",
                    value: isProgressive ? "Прогресивний" : "Послідовний",
                    info: isProgressive
                        ? {
                              type: SelfPlanModalType.progressiveScale,
                              payload: {
                                  title: name,
                                  currentPercent: percent,
                                  progressive: progressive ?? [],
                              },
                          }
                        : {
                              type: SelfPlanModalType.sequentialScale,
                              payload: {
                                  title: name,
                                  minComplete: minComplete ?? 0,
                                  maxComplete,
                              },
                          },
                };

            let productsItems: DashboardCollapseListItem<SelfPlanModalState>[] =
                [];
            if (isMotivationGroupP(group)) {
                const barcodes = group.context.barcodes;
                if (barcodes.length) {
                    productsItems = [
                        {
                            label: "Товари:",
                            value: `${barcodes.length} шт.`,
                            info: {
                                type: SelfPlanModalType.products,
                                payload: {
                                    title: countText.good(barcodes.length),
                                    barcodes,
                                },
                            },
                        },
                    ];
                }
            }

            const motivationItems: DashboardCollapseListItem<SelfPlanModalState>[] =
                [];
            if (isMotivationGroupP(group)) {
                motivationItems.push(
                    {
                        label: "Показник:",
                        value: (
                            <PrimaryText fontSize={28}>
                                {getMotivationImage(group.context.indicator)}
                            </PrimaryText>
                        ),
                    },
                    {
                        label: "Мін. кількість:",
                        value: formatValueIndicator({
                            value: group.context.minCount,
                            indicator: group.context.indicator,
                        }),
                    },
                );

                if (finGroupDetails.length <= 0) {
                    motivationItems.push({
                        label: "Унікальних СКЮ:",
                        value: `${group.context.minCountSKU} шт.`,
                    });
                }
            }

            if (isMotivationGroupA(group)) {
                motivationItems.push(
                    {
                        label: "Цільова середня СКЮ:",
                        value: `${formatNumber(
                            group.context.targetAverageSKU,
                        )} шт.`,
                    },
                    {
                        label: "За період:",
                        value: pluralizeUk(group.context.monthsPeriod, {
                            one: "# місяць",
                            few: "# місяці",
                            many: "# місяців",
                            other: "# місяців",
                        }),
                    },
                );
            }

            const specialItems: DashboardCollapseListItem<SelfPlanModalState>[] =
                groupType === "special"
                    ? [
                          {
                              label: "Коефіцієнт збільшення плану:",
                              value: planGrowthCoefficient,
                          },
                      ]
                    : [];

            return [
                ...baseItems,
                {
                    label: "План:",
                    value: `${formatNumber(planCount)} ${measurement}`,
                },
                {
                    label: "Факт:",
                    value: `${formatUAHNumber(factCount)} ${measurement}`,
                },
                ...motivationItems,
                ...specialItems,
                {
                    label: "Виконання плану:",
                    value: `${formatNumber(percent, 2)} %`,
                },
                scaleTypeItem,
                ...scaleItems,
                { label: "Бонус:", value: `${bonusValue} грн` },
                ...productsItems,
            ];
        }, [
            finGroupsItem,
            motivationType,
            bonus,
            isProgressive,
            name,
            percent,
            progressive,
            minComplete,
            maxComplete,
            group,
            planCount,
            factCount,
            finGroupDetails.length,
            groupType,
            planGrowthCoefficient,
            scaleItems,
            salaryPercent,
        ]);

    return collapseItems;
};
